import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import LeaderBoardRow from './leader-board-row'
import { giphyLightGrey, giphyWhite, giphyBlue, giphyIndigo, giphyDarkestGrey, giphyPurple } from '@giphy/js-brand'
import { Button } from 'shared/components/buttons'
import { mobile } from 'shared/util/media-query'
import { useMedia } from 'react-use'
import CsvModal from './csv-modal'

const LeaderboardContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`

const ShowMoreButton = styled(Button)`
    font-size: 16px;
    width: 420px;
    background: linear-gradient(-135deg, ${giphyIndigo} 0%, ${giphyPurple} 100%);
    ${mobile.css`
        width: 100%;
    `}
    border-radius: 4px;
`

const CsvButton = styled.button`
    display: flex;
    flex-direction: row;
    background: transparent;
    color: ${giphyWhite};
    font-family: interface;
    font-weight: bold;
    font-size: 18px;

    &:hover {
        color: ${giphyBlue};
    }
    i {
        margin-right: 20px;
        margin-top: -2px;
        position: relative;
        color: ${giphyBlue};
        width: 45px;
        height: 45px;
        border-radius: 50%;
        display: inline-block;
        border: 1px solid rgba(255, 255, 255, 0.15);
        line-height: 45px;
        text-align: center;
    }
`
const MoreActionsBar = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 8px;
    align-items: center;
    justify-content: center;
`

const AddMoreContentButton = styled.a`
    display: block;
    background-image: url('/static/img/dashboard/AddMoreButton@2x.png');
    background-size: cover;
    width: 137px;
    height: 137px;
    display: inline-block;
    margin: 0 auto 20px;
    ${mobile.css`
        width: 100px;
        height: 100px;
    `}
`

const NoDataMessageContainer = styled.div`
    margin-top: 140px;
    display: flex;
    flex-direction: column;
    text-align: center;
    font-family: 'interface';
    font-weight: bold;
    ${mobile.css`
        margin-top:60px;
    `}
    div {
        color: white;
    }
    span {
        color: ${giphyLightGrey};
    }
`

const DownloadContainer = styled.div`
    border-radius: 4px;
`

const DownloadButtonContainer = styled.div`
    margin-top: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${giphyDarkestGrey};
    color: ${giphyLightGrey};
    border-radius: 4px;
    height: 60px;
`

const DownloadTextContainer = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    text-align: center;
    font-size: 16px;
    span {
        color: ${giphyLightGrey};
    }
`

export interface ContentPiece {
    contentId: string
    viewCount: number
}

type Props = {
    csv: string
    csvCtr: string
    data: ContentPiece[]
    contentType: string
    dateRange: string[]
    datePreset: string
}

const NoDataMessage = () => {
    return (
        <NoDataMessageContainer>
            <AddMoreContentButton href="/upload" />
            <div>No data available!</div>
            <span>Try adjusting the date range, or upload more!</span>
        </NoDataMessageContainer>
    )
}
const listRows = (props: Props, limit: number) => {
    const { data, contentType, dateRange } = props
    if (data != null && data.length > 0) {
        // Calculate the maximum views displayed so we know how to draw the bar graph in relation to the max
        let maxViews = Math.max.apply(
            Math,
            data.map(function(o) {
                return o.viewCount
            })
        )
        data.sort(function(a, b) {
            return a.viewCount - b.viewCount
        }).reverse()
        // Iterate through all of the pieces of content and create a row on the leaderboard for each.
        return data.slice(0, limit).map((item, index) => {
            return (
                <LeaderBoardRow
                    key={index}
                    contentType={contentType}
                    maxViews={maxViews}
                    index={index}
                    item={item}
                    dateRange={dateRange}
                />
            )
        })
    } else {
        // If no data, show a fallback image and message.
        return <NoDataMessage />
    }
}

const LeaderBoardGraph = (props: Props) => {
    const isMobile = useMedia(mobile.query)
    const { data, csv, csvCtr } = props
    const [limit, setLimit] = useState<number>(5)
    const [moreContentAvailable, setMoreContentAvailable] = useState<boolean>(true)
    const [isModalActive, setIsModalActive] = useState<boolean>(false)

    // Refresh the leaderboard graph when new data is provided.
    useEffect(() => {
        if (data.length <= 5) {
            setMoreContentAvailable(false)
        }

        return () => {}
    }, [data])

    const increaseLimit = () => {
        if (moreContentAvailable) {
            setLimit(limit + 5)
            // If user tries to show more than 20 pieces of content, prompt them to download the CSV instead, even if more content is available.
            if (limit + 5 > data.length || limit >= 15) {
                setMoreContentAvailable(false)
            }
        }
    }
    return (
        <div>
            {isModalActive && <CsvModal csv={csv} csvCtr={csvCtr} onClose={() => setIsModalActive(false)} />}
            <LeaderboardContainer>{listRows(props, limit)}</LeaderboardContainer>
            {data && data.length > 0 && (
                <div>
                    <MoreActionsBar>
                        <ShowMoreButton
                            disabled={!moreContentAvailable}
                            secondary={!moreContentAvailable}
                            onClick={increaseLimit}
                        >
                            {moreContentAvailable ? 'Load 5 More' : 'Download CSV for full data'}
                        </ShowMoreButton>
                    </MoreActionsBar>
                    {(csv || csvCtr) && !isMobile ? (
                        <DownloadContainer>
                            <DownloadButtonContainer>
                                <CsvButton onClick={() => setIsModalActive(true)}>
                                    <i className={'ss-download'} />
                                    <span>Download Full CSV</span>
                                </CsvButton>
                            </DownloadButtonContainer>
                            <DownloadTextContainer>
                                <span>
                                    CSV Data includes GIF & Sticker views by day for all time, Engagement Rates{' '}
                                </span>
                                <span>by all tags since October 2019, Clips views by day for May 2021 and later.</span>
                            </DownloadTextContainer>
                        </DownloadContainer>
                    ) : null}
                </div>
            )}
        </div>
    )
}

export default LeaderBoardGraph
